/**
 * API keys — Step 52.
 *
 * Two kinds, and the split is the whole point (Kai): a **personal** key
 * borrows your permissions and dies with your account; an **application** key
 * has its own permissions, outlives whoever set it up, and signs its own name
 * in the audit log. A key tied to a person stops working the day they leave —
 * usually at 2am, in whatever integration nobody remembers exists.
 *
 * The token is shown ONCE. It is stored only as a hash, so there is no
 * "show again" to build; the dialog says so before you can close it.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, KeyRound, Plus } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Modal } from '../../components/ui/modal';
import { TokenSelect } from '../../components/ui/tokenselect';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { fullTime } from '../../lib/time';
import { cn } from '../../lib/utils';
import { useDirty } from '../../lib/dirty';
import { useConfirm } from '../../lib/confirm';

interface Row {
  id: string;
  kind: 'agent' | 'app';
  name: string;
  prefix: string;
  lastFour: string;
  roles: string[] | null;
  ownerName: string | null;
  ownerEmail: string | null;
  lastUsedAt: number | null;
  expiresAt: number | null;
  revokedAt: number | null;
  createdAt: number;
}

export function ApiKeysPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'agent' | 'app'>('agent');
  const [roleKeys, setRoleKeys] = useState<string[]>([]);
  const [issued, setIssued] = useState<string | null>(null);
  /** A half-named key is small work, but the guard is cheap and the audit is
   *  right that losing typing without a prompt is never the behaviour. */
  const clearDirty = useDirty(open && !!name.trim());

  const list = useQuery({
    queryKey: qk.apiKeys.all,
    queryFn: () => apiFetch<{ entries: Row[]; canManageApps: boolean }>('/api/api-keys'),
  });
  const roles = useQuery({
    queryKey: qk.apiKeys.roles,
    queryFn: () => apiFetch<{ entries: { key: string; name: string }[] }>('/api/api-keys/roles'),
    staleTime: 5 * 60_000,
  });

  const create = useMutation({
    mutationFn: () =>
      apiSend<{ id: string; token: string }>('/api/api-keys', 'POST', {
        name: name.trim(),
        kind,
        ...(kind === 'app' ? { roles: roleKeys } : {}),
      }),
    onSuccess: (r) => {
      if (!r.ok || !r.body) return toast.error(r.error ?? 'Could not create the key.');
      clearDirty();
      setIssued(r.body.token);
      setOpen(false);
      setName('');
      setRoleKeys([]);
      void qc.invalidateQueries({ queryKey: qk.apiKeys.all });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => apiSend(`/api/api-keys/${id}/revoke`, 'POST'),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not revoke.');
      void qc.invalidateQueries({ queryKey: qk.apiKeys.all });
    },
  });

  const entries = list.data?.entries ?? [];
  const canApps = !!list.data?.canManageApps;

  return (
    <div className="max-w-4xl space-y-4 p-6">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h1 className="text-lg font-semibold">API keys</h1>
          <p className="text-sm text-muted-foreground">
            Personal keys act as you. Application keys act as themselves, with their own permissions.
          </p>
        </div>
        <Button
          onClick={() => {
            setName('');
            setKind('agent');
            setRoleKeys([]);
            setOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          New key
        </Button>
      </div>

      {list.isPending && <Skeleton className="h-24 w-full" />}
      {list.data && entries.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 p-10 text-center">
            <KeyRound className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No keys yet.</p>
          </CardContent>
        </Card>
      )}

      {entries.length > 0 && (
        <Card>
          <CardContent className="p-0">
            {entries.map((k) => (
              <div key={k.id} className="flex items-center gap-3 border-b px-4 py-2.5 last:border-b-0">
                <div className="min-w-0 flex-1">
                  <p className={cn('truncate text-sm font-medium', k.revokedAt && 'line-through opacity-60')}>
                    {k.name}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {/* Enough to tell two keys apart, and not enough to use. */}
                    itsm_{k.prefix}…{k.lastFour} ·{' '}
                    {k.kind === 'app'
                      ? `roles: ${(k.roles ?? []).join(', ') || 'none'}`
                      : (k.ownerName ?? k.ownerEmail ?? 'you')}
                    {k.lastUsedAt ? ` · last used ${fullTime(k.lastUsedAt)}` : ' · never used'}
                  </p>
                </div>
                <Badge variant="outline" className={k.kind === 'app' ? 'border-brand/40 text-brand' : ''}>
                  {k.kind === 'app' ? 'Application' : 'Personal'}
                </Badge>
                {k.revokedAt ? (
                  <Badge variant="outline" className="text-muted-foreground">
                    Revoked
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      if (
                        await confirm({
                          title: `Revoke “${k.name}”?`,
                          body: 'Anything using it stops working immediately.',
                          destructive: true,
                          confirmLabel: 'Revoke',
                        })
                      )
                        revoke.mutate(k.id);
                    }}
                  >
                    Revoke
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New API key">
        <form
          className="space-y-3 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="What will use this key?"
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Kind</label>
            <Select value={kind} onChange={(e) => setKind(e.target.value as 'agent' | 'app')}>
              <option value="agent">Personal — acts as me</option>
              <option value="app" disabled={!canApps}>
                Application — acts as itself{canApps ? '' : ' (needs admin)'}
              </option>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              {kind === 'app'
                ? 'Keeps working when people leave, and signs its own name in the audit log.'
                : 'Uses your permissions, and stops working if your account does.'}
            </p>
          </div>
          {kind === 'app' && (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">What it may do</label>
              <TokenSelect
                value={roleKeys}
                onChange={setRoleKeys}
                ariaLabel="Roles"
                options={(roles.data?.entries ?? []).map((r) => ({ value: r.key, label: r.name }))}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Give it the least it needs — a key with no roles cannot do anything at all.
              </p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                clearDirty();
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || create.isPending || (kind === 'app' && !roleKeys.length)}
            >
              {create.isPending ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Shown once, because only a hash is stored — there is no "show again"
          to build, and saying so up front is kinder than a support ticket. */}
      <Modal open={!!issued} onClose={() => setIssued(null)} title="Copy this key now">
        <div className="space-y-3 p-4">
          <p className="text-sm">
            This is the only time it will be shown. We store a hash, not the key, so it cannot be retrieved
            later — only replaced.
          </p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 overflow-x-auto rounded-md border bg-muted px-3 py-2 text-xs">
              {issued}
            </code>
            <Button
              onClick={() => {
                void navigator.clipboard?.writeText(issued ?? '');
                toast.success('Copied.');
              }}
            >
              <Copy className="h-4 w-4" />
              Copy
            </Button>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setIssued(null)}>
              Done
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
